import supportedLanguages, { languageModuleMap } from './supportedLanguages'
import { ILocaleData } from './ILocaleData'
import { LocaleCompatibility } from './LocaleCompatibility'
import { LocalizationString } from './localization-strings'
import { ILanguageInformation } from './supportedLanguages'
import { Log } from '../shared/logger/Log'
import { LocalizationActivity } from '../shared/LocalizationActivity'
export type TextDirectionType = 'ltr' | 'rtl'

interface ILocaleWithCompatibility {
  locale: string,
  points: LocaleCompatibility
}

export class LocalizationService {
  private static _locale: string
  private static _localeInfo: ILanguageInformation

  private static translationsCache: { [locale: string]: { [stringId: string]: string} } = {}
  public static readonly DEFAULT_LOCALE = 'en-US'

  static get areLocalizationValuesReady(): boolean {
    if (this._locale && this._localeInfo) {
      return true
    }
    return false
  }

  static get locale() {
    return this._locale
  }

  static get textDirection(): TextDirectionType {
    return this._localeInfo.isRtl ? 'rtl' : 'ltr'
  }

  static get isRtl(): boolean {
    return this._localeInfo.isRtl ? this._localeInfo.isRtl : false
  }

  /**
   * Gets supported locale from a rawLocale string
   */
  static getSupportedLocale(rawLocale: string): ILocaleWithCompatibility {
    // Supported languages (EOL) support specified in https://setup.intlservices.microsoft.com/Home/ConfigSettings?TeamID=520
    let bestSoFar: ILocaleWithCompatibility = { points: LocaleCompatibility.NO_MATCH, locale: LocalizationService.DEFAULT_LOCALE }
    for (const supportedLocale of Object.keys(supportedLanguages)) {
      const compatibilityValue = LocalizationService.getLocaleCompatibilityValue(supportedLocale, rawLocale)
      if (compatibilityValue === LocaleCompatibility.EXACT_MATCH) {
        return { locale: supportedLocale, points: LocaleCompatibility.EXACT_MATCH }
      }
      if (compatibilityValue > bestSoFar.points) {
        bestSoFar = { points: compatibilityValue, locale: supportedLocale }
      }
    }

    return bestSoFar
  }

  private static getLocaleCompatibilityValue(systemLocale: string, desiredLocale: string): LocaleCompatibility {
    if (!systemLocale || !desiredLocale) {
      return LocaleCompatibility.NO_MATCH
    }

    const [desiredLang, desiredCulture] = desiredLocale.toLocaleLowerCase().split('-')
    const [lang, culture] = systemLocale.toLocaleLowerCase().split('-')

    if (lang === desiredLang && culture === desiredCulture) {
      return LocaleCompatibility.EXACT_MATCH
    } else if (lang === desiredLang && !culture) {
      return LocaleCompatibility.GOOD_MATCH
    } else if (lang === desiredLang) {
      return LocaleCompatibility.POOR_MATCH
    }
    return LocaleCompatibility.NO_MATCH
  }

  static async changeLocale(locale: string): Promise<void> {
    locale = languageModuleMap[locale] ?? locale
    const supportedLocaleInfo = LocalizationService.getSupportedLocale(locale)
    const localizationActivity = new LocalizationActivity(locale, supportedLocaleInfo.locale, supportedLocaleInfo.points)

    try {
      if (!LocalizationService.translationsCache[supportedLocaleInfo.locale]) {
        const currentLocalePromise = import(`./locales/${supportedLocaleInfo.locale}/index`) as Promise<ILocaleData>
  
        // This is done to prevent double-loading of the same set of strings
        const defaultLocalePromise = supportedLocaleInfo.locale === LocalizationService.DEFAULT_LOCALE
          ? Promise.resolve({ translations: {} })
          : import(`./locales/${LocalizationService.DEFAULT_LOCALE}/index`) as Promise<ILocaleData>
  
        const currentLocaleData = await currentLocalePromise
        const defaultLocaleData = await defaultLocalePromise
  
        LocalizationService.translationsCache[supportedLocaleInfo.locale] = { ...defaultLocaleData.translations, ...currentLocaleData.translations }
        LocalizationService.updateLocale(supportedLocaleInfo.locale)
        localizationActivity.finishWithSuccess()
      } else {
        LocalizationService.updateLocale(supportedLocaleInfo.locale)
        localizationActivity.finishWithFailure('LocaleShouldNotBeChangedMoreThanOnce')
      }
    } catch(ex) {
      localizationActivity.finishWithFailure('ErrorDuringLocalization', ex)
    }
  }

  private static updateLocale(locale: string) {
    LocalizationService._locale = locale
    LocalizationService._localeInfo = this.getSupportedLocaleInfo(locale)

    const htmlElement = document.getElementsByTagName('html')[0]
    if (htmlElement && htmlElement.setAttribute) {
      const lang = locale.split('-')[0]
      htmlElement.setAttribute('lang', lang)
      htmlElement.setAttribute('dir',LocalizationService.textDirection)
    }
  }

  static getLocalizedString(id: LocalizationString): string {
    const translations = LocalizationService.translationsCache[this._locale]
    if (!translations) {
      Log.logger.error('Translations for this locale have not been fetched yet! Be sure to await changeLocale before calling getLocalizedString!')
      return ''
    }

    const str = translations[id]
    if (!str) {
      Log.logger.error('The requested id has no matching localized string or fallback.')
    }
    return str || ''
  }

  static getSupportedLocaleInfo(locale: string): ILanguageInformation {
    return supportedLanguages[locale]
  }
}